import React, {useEffect, useMemo, useState} from 'react';
import {CustomCard} from "../../../../../../components/UI/AntCustom/CustomCard";
import {useAppDispatch, useAppSelector} from "../../../../../../store/store";
import styled from "styled-components";
import {roles} from "../../../../../../config/acl/roles";
import EmailIcon from "../../../../../../media/icons/email.svg?react";
import PhoneIcon from "../../../../../../media/icons/phone.svg?react";
import Settings from "../../../../../../media/icons/settings.svg?react";
import Button from "antd/es/button/button";
import {CardBottomButtons} from "../../../../../../components/UI/AntCustom/CardBottomButtons";
import {groupUserAdmin, groupFreighter, groupCharterer} from "../../../../../../config/acl/roles/groups";
import {EditUserModal} from "./EditUserModal";
import {ChangeUserPasswordModal} from "./ChangeUserPasswordModal";
import {UserSettingsDrawer} from "./UserSettingsDrawer";
import {DisplayFullName} from "../../../../../../utils/helpers/textDisplayHelpers/DisplayFullName";
import {defineMessages, useIntl} from "react-intl";
import {CommonDetailsMessages} from "../../../../../../intl/commonMessages/CommonDetailsMessages";
import {CommonButtonMessages} from "../../../../../../intl/commonMessages/CommonButtonMessages";
import {useIsMobile} from "../../../../../../hooks/useWindowSize";
import {getTariff, clearTariff} from "../../../../../../store/reducers/tariffReducer/tariffReducer";
import dayjs from "dayjs";


const ProfileUserTitleWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  column-gap: 32px;
`;

const ProfileUserSettingsButton = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 8px;
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  color: #000000;
  transition: 0.2s ease;
  cursor: pointer;

  :hover {
    opacity: 0.7;
  }
`;

const ProfileUserWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 24px;
`;

const SectionTitle = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #6B6B6B;
`;

const RoleAndFullName = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 3px;
`;
const FullName = styled.div`
  font-weight: 600;
  font-size: 20px;
  line-height: 22px;
  color: #000000;
`;

const Contacts = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  overflow-x: hidden;
`;

const ContactsContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  column-gap: 42px;
  row-gap: 8px;
  overflow-x: hidden;
`;

const ContactIconAndValue = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;

  div {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    color: #000000;
  }
`;

const EmailValue = styled.div<{maxWidth?: string}>`
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  color: #000000;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 400px;
`;


const Subscription = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 5px;
`;

const SubscriptionStatus = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 8px;

  div {
    font-weight: 500;
    font-size: 16px;
    line-height: 22px;
    color: #000000;
  }

  span {
    width: 10px;
    height: 10px;
    background: #31CA91;
    border-radius: 100%;
  }
`;

const SubscriptionDescription = styled.div`
  margin-top: 11px;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #6B6B6B;
  letter-spacing: -0.01em;
  max-width: 365px;
`;

type TrialStatusType = 'active' | 'expiringSoon' | 'expired';

type SubscriptionStatusType = 'active' | 'cancelledValid' | 'ended';

const TrialStatus = styled.div<{status: TrialStatusType}>`
  display: flex;
  flex-direction: column;
  row-gap: 5px;
  margin-top: 11px;
  
  div {
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    color: ${props => {
      if (props.status === 'expired') return '#FF6B6B';
      return '#000000';
    }};
    letter-spacing: -0.01em;
  }
`;

const SubscriptionStatusText = styled.div<{status: SubscriptionStatusType}>`
  margin-top: 11px;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: ${props => {
    if (props.status === 'ended') return '#FF6B6B';
    if (props.status === 'cancelledValid') return '#000000';
    return '#000000';
  }};
  letter-spacing: -0.01em;
`;


const ProfileUserMessages = defineMessages({
  title: {
    id: "profileUser.title",
    defaultMessage: "Profile",
  },
  settings: {
    id: "profileUser.settings",
    defaultMessage: "Settings",
  },
  contactInfo: {
    id: "profileUser.contactInfo",
    defaultMessage: "Contact information",
  },
  subscriptionDescription: {
    id: "profileUser.subscriptionDescription",
    defaultMessage: "Jetsnode is free for all users till the end of 2023. Thank you for helping us develop our service!",
  },
  changePassword: {
    id: "profileUser.changePassword",
    defaultMessage: "Change password",
  },
  trialMode: {
    id: "profileUser.trialMode",
    defaultMessage: "Trial mode",
  },
  trialEndsOn: {
    id: "profileUser.trialEndsOn",
    defaultMessage: "Trial ends on {date}",
  },
  subscriptionActive: {
    id: "profileUser.subscriptionActive",
    defaultMessage: "Subscription active",
  },
  subscriptionUntil: {
    id: "profileUser.subscriptionUntil",
    defaultMessage: "Subscription until {date} (cancelled)",
  },
  subscriptionEnded: {
    id: "profileUser.subscriptionEnded",
    defaultMessage: "Subscription ended",
  },
  subscriptionValidUntil: {
    id: "profileUser.subscriptionValidUntil",
    defaultMessage: "Valid until {date}",
  },
  subscriptionEndedOn: {
    id: "profileUser.subscriptionEndedOn",
    defaultMessage: "Subscription ended on {date}",
  },
})

export const ProfileUser = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();
  const user = useAppSelector(state => state.user.oneItemObject.item)
  const tariff = useAppSelector(state => state.tariff.oneItemObject.item)
  const isAdmin = groupUserAdmin.includes(user.role);
  const isOperatorOrBroker = groupFreighter.includes(user.role) || groupCharterer.includes(user.role);
  const [isUserSettingsDrawerOpen, setIsUserSettingsDrawerOpen] = useState<boolean>(false);
  const [isUserEditModalOpen, setIsUserEditModalOpen] = useState<boolean>(false);
  const [isUserChangePasswordModalOpen, setIsUserChangePasswordModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (isOperatorOrBroker) {
      dispatch(getTariff({}));
    }
    return () => {
      if (isOperatorOrBroker) {
        dispatch(clearTariff());
      }
    };
  }, [isOperatorOrBroker]);

  const activePackage = useMemo(() => {
    if (!tariff?.companyTariffPackages?.length) return null;
    return tariff.companyTariffPackages.find(pkg => pkg.active) || null;
  }, [tariff]);

  const hasSubscription = Boolean(activePackage?.stripeSubscriptionId);
  const isTrialing = activePackage?.subscriptionStatus === 'trialing';

  // Show Trial only when: actually trialing, or trial-only (no subscription ever)
  const showTrial = useMemo(() => {
    if (!activePackage) return false;
    if (isTrialing) return true;
    if (activePackage.isTrial && !hasSubscription) return true;
    return false;
  }, [activePackage, isTrialing, hasSubscription]);

  const trialEndDate = useMemo(() => {
    if (!showTrial || !activePackage) return null;
    return dayjs(activePackage.tariffEnd || activePackage.trialEndAt);
  }, [showTrial, activePackage]);

  const trialStatus = useMemo<TrialStatusType>(() => {
    if (!trialEndDate) return 'active';
    const daysUntilEnd = trialEndDate.diff(dayjs(), 'day');
    if (daysUntilEnd <= 0) return 'expired';
    if (daysUntilEnd <= 14) return 'expiringSoon';
    return 'active';
  }, [trialEndDate]);

  const trialIndicatorColor = useMemo(() => {
    if (trialStatus === 'expired') return '#FF6B6B';
    if (trialStatus === 'expiringSoon') return '#F9D99B';
    return '#6B6B6B';
  }, [trialStatus]);

  // Subscription display: end date and status
  const subscriptionEndDate = useMemo(() => {
    if (!activePackage || !hasSubscription) return null;
    const end = activePackage.currentPeriodEnd || activePackage.tariffEnd || activePackage.trialEndAt;
    return end ? dayjs(end) : null;
  }, [activePackage, hasSubscription]);

  const subscriptionStatus = useMemo<SubscriptionStatusType>(() => {
    if (!subscriptionEndDate) return 'active';
    if (subscriptionEndDate.isBefore(dayjs())) return 'ended';
    const isCancelled = !activePackage?.autoRenewal || activePackage?.subscriptionStatus === 'canceled';
    if (isCancelled) return 'cancelledValid';
    return 'active';
  }, [subscriptionEndDate, activePackage]);

  const subscriptionIndicatorColor = useMemo(() => {
    if (subscriptionStatus === 'ended') return '#FF6B6B';
    if (subscriptionStatus === 'cancelledValid') return '#F9D99B';
    return '#31CA91';
  }, [subscriptionStatus]);

  const cardTitle = <ProfileUserTitleWrapper>
    <div>{intl.formatMessage(ProfileUserMessages.title)}</div>

    <ProfileUserSettingsButton onClick={() => setIsUserSettingsDrawerOpen(true)}>
      <Settings/>
      <div>{intl.formatMessage(ProfileUserMessages.settings)}</div>
    </ProfileUserSettingsButton>
  </ProfileUserTitleWrapper>

  return (
    <ProfileUserWrapper>
      <CustomCard title={cardTitle} width={'max'} isContainerCard={true} maxWidth={isMobile ? undefined : 700} noBottomBorderRadius={isAdmin}
                  borderless={true}
                  style={{flexGrow: 1}}>
        <ContentWrapper>
          <RoleAndFullName>
            <SectionTitle>{intl.formatMessage(roles[user.role].name)}</SectionTitle>
            <FullName>{DisplayFullName(user)}</FullName>
          </RoleAndFullName>

          <Contacts>
            <SectionTitle>{intl.formatMessage(ProfileUserMessages.contactInfo)}</SectionTitle>
            <ContactsContent>
              <ContactIconAndValue>
                <PhoneIcon/>
                <div>+{user.phone}</div>
              </ContactIconAndValue>
              <ContactIconAndValue>
                <EmailIcon/>
                <EmailValue title={user.email}>{user.email}</EmailValue>
              </ContactIconAndValue>
            </ContactsContent>
          </Contacts>

          <Subscription>
            {showTrial ? (
              <>
                <SubscriptionStatus>
                  <span style={{background: trialIndicatorColor}}/>
                  <div>{intl.formatMessage(ProfileUserMessages.trialMode)}</div>
                </SubscriptionStatus>
                {trialEndDate && (
                  <TrialStatus status={trialStatus}>
                    <div>
                      {intl.formatMessage(ProfileUserMessages.trialEndsOn, {
                        date: trialEndDate.format('DD.MM.YYYY')
                      })}
                    </div>
                  </TrialStatus>
                )}
              </>
            ) : hasSubscription ? (
              <>
                <SubscriptionStatus>
                  <span style={{background: subscriptionIndicatorColor}}/>
                  <div>{intl.formatMessage(ProfileUserMessages.subscriptionActive)}</div>
                </SubscriptionStatus>
                {(subscriptionEndDate || subscriptionStatus === 'ended') && (
                  <SubscriptionStatusText status={subscriptionStatus}>
                    {subscriptionStatus === 'ended' && (
                      <div>
                        {subscriptionEndDate
                          ? intl.formatMessage(ProfileUserMessages.subscriptionEndedOn, {
                              date: subscriptionEndDate.subtract(1, 'day').format('DD.MM.YYYY')
                            })
                          : intl.formatMessage(ProfileUserMessages.subscriptionEnded)}
                      </div>
                    )}
                    {subscriptionStatus === 'cancelledValid' && subscriptionEndDate && (
                      <div>
                        {intl.formatMessage(ProfileUserMessages.subscriptionUntil, {
                          date: subscriptionEndDate.subtract(1, 'day').format('DD.MM.YYYY')
                        })}
                      </div>
                    )}
                    {subscriptionStatus === 'active' && subscriptionEndDate && (
                      <div>
                        {intl.formatMessage(ProfileUserMessages.subscriptionValidUntil, {
                          date: subscriptionEndDate.subtract(1, 'day').format('DD.MM.YYYY')
                        })}
                      </div>
                    )}
                  </SubscriptionStatusText>
                )}
              </>
            ) : (
              <>
                <SubscriptionStatus>
                  <span/>
                  <div>{intl.formatMessage(CommonDetailsMessages.subscriptionActive)}</div>
                </SubscriptionStatus>
                <SubscriptionDescription>
                  {intl.formatMessage(ProfileUserMessages.subscriptionDescription)}
                </SubscriptionDescription>
              </>
            )}
          </Subscription>
        </ContentWrapper>
      </CustomCard>
      {isAdmin &&
        <CardBottomButtons>
          <Button ghost={true} type={"primary"} onClick={() => setIsUserChangePasswordModalOpen(true)}>{intl.formatMessage(ProfileUserMessages.changePassword)}</Button>
          <Button type={'primary'} onClick={() => setIsUserEditModalOpen(true)}>{intl.formatMessage(CommonButtonMessages.edit)}</Button>
        </CardBottomButtons>}

      {isUserEditModalOpen &&
        <EditUserModal isModalVisible={isUserEditModalOpen} setIsModalVisible={setIsUserEditModalOpen}/>}

      {isUserChangePasswordModalOpen &&
        <ChangeUserPasswordModal isModalVisible={isUserChangePasswordModalOpen}
                                 setIsModalVisible={setIsUserChangePasswordModalOpen}/>}

      {isUserSettingsDrawerOpen &&
        <UserSettingsDrawer isOpen={isUserSettingsDrawerOpen} setIsOpen={setIsUserSettingsDrawerOpen}/>}
    </ProfileUserWrapper>
  );
};
